import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../../lib/supabase';
import { HOTEL_ID } from '../../lib/supabase/configClient';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { Bell, X } from 'lucide-react';
import { resolveColor } from '../../utils/colorUtils';
import { stayService } from '../../lib/stay/stayService';
import { navigationClient } from '../../lib/navigation';

interface AlertTranslation {
  language: string;
  content: string;
}

interface Alert {
  id: string;
  content: string;
  url: string | null;
  title: string;
  start_time: string | null;
  end_time: string | null;
  translations: AlertTranslation[];
  color_alert_back: string | null;
  color_alert_txt: string | null;
  banner_size: 'small' | 'big' | null;
  alert_type: 'standard' | 'checkout' | null;
  action_type: 'url' | 'section' | null;
  action_section_id: string | null;
}

export const ActiveAlertBanner: React.FC<{ config: any }> = ({ config }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<Alert | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [isPwaPromptVisible, setIsPwaPromptVisible] = useState(false);
  const [checkoutAlertShown, setCheckoutAlertShown] = useState(false);

  const { data: navigation } = useQuery({
    queryKey: ['navigation'],
    queryFn: () => navigationClient.getNavigation(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000
  });

  useEffect(() => {
    const hasVisited = localStorage.getItem('has_visited');
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    setIsPwaPromptVisible(!hasVisited && !isPWA);
  }, []);

  const isCheckoutDay = (): boolean => {
    const stayDates = stayService.getStayDates();
    if (!stayDates) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(stayDates.endDate);
    endDate.setHours(0, 0, 0, 0);
    return today.getTime() === endDate.getTime();
  };

  const hasCheckoutAlertBeenShownToday = (): boolean => {
    return stayService.hasCheckoutAlertBeenShownToday();
  };

  const markCheckoutAlertAsShown = (): void => {
    stayService.markCheckoutAlertAsShown();
    setCheckoutAlertShown(true);
  };

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setIsLoading(true);
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now.toTimeString().split(' ')[0];
        const shouldShowCheckoutAlert = isCheckoutDay() && !hasCheckoutAlertBeenShownToday();

        const response = await supabaseClient.rpc('get_active_alerts', {
          p_hotel_id: HOTEL_ID,
          p_date: currentDate,
          p_time: currentTime,
          p_checkout: shouldShowCheckoutAlert
        });

        const { data, error } = response;

        if (error) throw error;

        if (data && data.length > 0) {
          if (data[0].alert_type === 'checkout') markCheckoutAlertAsShown();
          setAlert(data[0]);
          analyticsService.trackEvent({
            event_type: 'impression',
            event_name: 'alert_banner_shown',
            event_data: {
              alert_id: data[0].id,
              has_url: !!data[0].url,
              banner_size: data[0].banner_size || 'small',
              alert_type: data[0].alert_type || 'standard',
              action_type: data[0].action_type || 'url'
            }
          });
        } else {
          setAlert(null);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
        setAlert(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlerts();
    const intervalId = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (alert) {
      // logic for updating alert content on language change can go here
    }
  }, [i18n.language, alert]);

  if (isLoading || !alert || dismissed) return null;
  if (alert.banner_size === 'big' && isPwaPromptVisible) return null;

  const translatedContent = alert.translations?.find(
    (t) => t.language === i18n.language
  )?.content || alert.content;

  const backgroundColor = resolveColor(alert.color_alert_back, config, config['major-color']);
  const textColor = resolveColor(alert.color_alert_txt, config, config['light-color']);

  const handleClick = () => {
    if (alert.action_type === 'section' && alert.action_section_id && navigation) {
      const section = navigation.find(item => item.id === alert.action_section_id);
      if (section) {
        analyticsService.trackEvent({
          event_type: 'interaction',
          event_name: 'alert_banner_click',
          event_data: {
            alert_id: alert.id,
            action_type: 'section',
            section_id: alert.action_section_id,
            section_name: section.names.fr,
            section_type: section.type_rubrique,
            banner_size: alert.banner_size || 'small',
            alert_type: alert.alert_type || 'standard'
          }
        });
        setDismissed(true);
        const slug = navigationClient.generateSlug(section.names.fr);
        navigate(`/${slug}`);
      }
    } else if (alert.url) {
      analyticsService.trackEvent({
        event_type: 'interaction',
        event_name: 'alert_banner_click',
        event_data: {
          alert_id: alert.id,
          url: alert.url,
          action_type: 'url',
          banner_size: alert.banner_size || 'small',
          alert_type: alert.alert_type || 'standard'
        }
      });
      setDismissed(true);
      window.open(alert.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'alert_banner_dismiss',
      event_data: {
        alert_id: alert.id,
        banner_size: alert.banner_size || 'small',
        alert_type: alert.alert_type || 'standard',
        action_type: alert.action_type || 'url'
      }
    });
    setDismissed(true);
  };

  const isClickable = alert.url || (alert.action_type === 'section' && alert.action_section_id);

  if (alert.banner_size === 'big') {
    return (
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black bg-opacity-50"
        onClick={handleDismiss}
      >
        <div
          className={`max-w-lg w-full p-6 rounded-lg shadow-xl text-center ${
            isClickable ? 'cursor-pointer hover:brightness-95' : ''
          }`}
          style={{ 
            backgroundColor,
            color: textColor,
            borderRadius: config.border_radius
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (isClickable) handleClick();
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <Bell size={24} className="shrink-0" />
            <div className="mx-auto"></div>
            <button
              onClick={handleDismiss}
              className="shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close alert"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mt-4 pb-4 text-xl font-bold">
            {translatedContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`fixed top-[50px] left-0 right-0 z-[150] py-3 px-4 transition-all duration-200 ${
        isClickable ? 'cursor-pointer hover:brightness-95' : ''
      }`}
      style={{ 
        backgroundColor,
        color: textColor
      }}
      onClick={isClickable ? handleClick : undefined}
    >
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-left">
          <Bell size={16} className="shrink-0" />
          <span>{translatedContent}</span>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
          aria-label="Close alert"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
